import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  InputGroup,
  Button,
  Modal,
} from "react-bootstrap";
import { FaSearch, FaPlus, FaExclamationTriangle } from "react-icons/fa";
import Joyride, { STATUS } from "react-joyride";
import { FaQuestionCircle } from "react-icons/fa";
import SupplierCard from "./SupplierCard";
import SupplierDetailsModal from "./SupplierDetailsModal";
import AddSupplierModal from "./AddSupplierModal";
import "./SupplierDetailsModal.css";

// Define modal types as constants
const MODAL_TYPES = {
  NONE: "none",
  DETAILS: "details",
  EDIT: "edit",
  DELETE: "delete",
};

const SuppliersPane = () => {
  // Main state management
  const [suppliers, setSuppliers] = useState([
    {
      id: 3,
      name: "מילמן דור ההמשך",
      category: "גביעים ומדליות",
      rating: 4.9,
      isGolden: true,
      description: "ספק מוביל לכלל סוגי הגביעים, המדליות ומגיני ההוקרה. ",
      tags: ["אותות הוקרה", "מדליות", "גביעים", "מגיני הוקרה"],
      phone: "077-4446707",
      email: "info@milman.co.il",
      address: "המפעל 1, תל אביב",
      website: "https://milmanltd.co.il/",
      imageUrl:
        "https://images.unsplash.com/photo-1549719386-74dfcbf7dbed?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
    },
  ]);
  const [currentSupplier, setCurrentSupplier] = useState(null);
  const [currentModal, setCurrentModal] = useState(MODAL_TYPES.NONE);
  const [modalPosition, setModalPosition] = useState(null);

  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("הכל");

  const categories = [
    "הכל",
    ...new Set(suppliers.map((supplier) => supplier.category)),
  ];

  const filteredSuppliers = suppliers
    .filter((supplier) => {
      const searchTermLower = searchTerm.toLowerCase();
      const matchesSearch =
        supplier.name.toLowerCase().includes(searchTermLower) ||
        supplier.description.toLowerCase().includes(searchTermLower) ||
        supplier.tags.some((tag) =>
          tag.toLowerCase().includes(searchTermLower)
        );
      const matchesCategory =
        selectedCategory === "הכל" || supplier.category === selectedCategory;
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => {
      // Sort by golden status first, then by rating
      if (a.isGolden && !b.isGolden) return -1;
      if (!a.isGolden && b.isGolden) return 1;
      return b.rating - a.rating; // Then sort by rating
    });

  // Modal management functions
  const openModal = (modalType, supplier = null, position = null) => {
    setCurrentSupplier(supplier);
    setCurrentModal(modalType);
    if (position) setModalPosition(position);
  };

  const closeModal = () => {
    setCurrentModal(MODAL_TYPES.NONE);
    setCurrentSupplier(null);
  };

  // Event handlers
  const handleCardClick = (supplier, position) => {
    openModal(MODAL_TYPES.DETAILS, supplier, position);
  };

  const handleAddSupplier = (newSupplier) => {
    // Mark new suppliers as personal
    const personalSupplier = {
      ...newSupplier,
      isPersonal: true,
    };
    setSuppliers([...suppliers, personalSupplier]);
    closeModal();
  };

  const handleEditClick = (supplier) => {
    // Only allow editing personal suppliers
    if (!supplier.isPersonal) {
      return;
    }
    openModal(MODAL_TYPES.EDIT, supplier);
  };

  const handleUpdateSupplier = (updatedSupplier) => {
    // Update the suppliers array
    setSuppliers(
      suppliers.map((supplier) =>
        supplier.id === updatedSupplier.id ? updatedSupplier : supplier
      )
    );

    // Close the edit modal and open the details modal with the updated supplier
    openModal(MODAL_TYPES.DETAILS, updatedSupplier);
  };

  const handleDeleteClick = (supplier) => {
    openModal(MODAL_TYPES.DELETE, supplier);
  };

  const handleConfirmDelete = () => {
    if (currentSupplier) {
      // Remove the supplier from the array
      setSuppliers(
        suppliers.filter((supplier) => supplier.id !== currentSupplier.id)
      );
      closeModal();
    }
  };

  const [runTour, setRunTour] = useState(false);

  // Tour button style
  const tourButtonStyle = {
    position: "absolute",
    top: "20px",
    left: "20px",
    backgroundColor: "#0066CC",
    color: "white",
    border: "none",
    borderRadius: "50%",
    width: "40px",
    height: "40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "all 0.3s ease",
    zIndex: 100,
    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
  };

  // Define tour steps
  const steps = [
    {
      target: ".suppliers-container",
      content:
        "ברוכים הבאים למסך הספקים! כאן תוכלו ליצור קשר עם ספקים מכלל התחומים שנוגעים למועדוני ספורט ולצפות בפרטיהם. בואו נעשה סיור קצר!",
      placement: "center",
      disableBeacon: true,
    },
    {
      target: ".search-input",
      content: "כאן תוכלו לחפש ספקים לפי שם, קטגוריה או מוצרים.",
      placement: "bottom",
    },
    {
      target: ".category-select",
      content: "בחרו קטגוריה כדי לסנן את הספקים לפי תחומים.",
      placement: "bottom",
    },
    {
      target: ".add-supplier-button",
      content: "כאן ניתן להוסיף ספקים אישיים שאתם עובדים איתם.",
      placement: "bottom",
    },
    {
      target: ".supplier-card",
      content: "לחצו על ספק כדי לצפות בפרטים המלאים שלו.",
      placement: "right",
    },
    {
      target: ".suppliers-guide-button",
      content: "אם תרצו לראות את הסיור שוב, תוכלו ללחוץ על כפתור זה בכל עת!",
      placement: "left",
      spotlightClicks: true,
    },
  ];

  // Handle tour events
  const handleJoyrideCallback = (data) => {
    const { status } = data;
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      setRunTour(false);
    }
  };

  const handleTourButtonClick = () => {
    setRunTour(true);
  };

  // Modern background and container styles
  const mainBg = {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #f8fafc 0%, #e3e9f7 100%)",
    padding: "32px 0",
  };
  const containerStyle = {
    maxWidth: 1200,
    margin: "0 auto",
    padding: "0 16px",
    position: "relative",
  };

  return (
    <div style={mainBg}>
      <style>{`
        .modern-card {
          border-radius: 16px !important;
          box-shadow: 0 2px 12px rgba(0,0,0,0.06) !important;
          background: #fff !important;
          transition: box-shadow 0.2s, transform 0.2s;
        }
        .modern-card:hover {
          box-shadow: 0 4px 24px #1976d233 !important;
          transform: translateY(-2px) scale(1.01);
        }
        .modern-title {
          font-weight: 800;
          font-size: 2.5rem;
          letter-spacing: 1px;
          margin-bottom: 24px;
          padding-left: 0;
          text-align: center;
        }
        .modern-add-btn {
          border-radius: 999px !important;
          font-weight: 700 !important;
          font-size: 1.2rem !important;
          padding: 16px 40px !important;
          box-shadow: 0 2px 8px rgba(0,180,80,0.10) !important;
        }
        .modern-form-input, .modern-form-input:focus {
          border-radius: 10px !important;
          box-shadow: 0 1px 4px rgba(0,0,0,0.04) !important;
          border: 1px solid #e0e0e0 !important;
          background: #fff !important;
        }
        .modern-search-input {
          border-radius: 10px !important;
          border: 1px solid #e0e0e0 !important;
          box-shadow: 0 1px 4px rgba(0,0,0,0.04) !important;
          background: #fff !important;
          font-size: 1.1rem !important;
          padding: 12px 16px !important;
        }
        .modern-category-select {
          border-radius: 10px !important;
          border: 1px solid #e0e0e0 !important;
          box-shadow: 0 1px 4px rgba(0,0,0,0.04) !important;
          background: #fff !important;
          font-size: 1.1rem !important;
          padding: 12px 16px !important;
        }
        .modern-supplier-card {
          border-radius: 16px !important;
          box-shadow: 0 2px 12px rgba(0,0,0,0.06) !important;
          background: #fff !important;
          transition: box-shadow 0.2s, transform 0.2s;
          padding: 20px 18px 16px 18px !important;
          margin-bottom: 24px !important;
        }
        .modern-supplier-card:hover {
          box-shadow: 0 4px 24px #1976d233 !important;
          transform: translateY(-2px) scale(1.01);
        }
        .modern-empty-message {
          color: #aaa;
          font-size: 1.1rem;
          font-style: italic;
        }
        .modern-modal .modal-content {
          border-radius: 18px !important;
        }
        .modern-modal .modal-header {
          border-bottom: none !important;
        }
        .modern-modal .modal-footer {
          border-top: none !important;
        }
        .modern-modal .btn {
          border-radius: 999px !important;
          font-weight: 700 !important;
          font-size: 1.1rem !important;
          padding: 10px 28px !important;
        }
        .modern-modal .btn-danger {
          background: #dc2626 !important;
          border: none !important;
        }
        .modern-modal .btn-secondary {
          background: #e2e8f0 !important;
          color: #333 !important;
          border: none !important;
        }
        .modern-modal .btn-primary {
          background: #0066CC !important;
          border: none !important;
        }
        .modern-tour-btn {
          position: absolute;
          top: 20px;
          left: 20px;
          background-color: #0066CC;
          color: white;
          border: none;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
          z-index: 100;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
        .modern-tour-btn:hover {
          transform: scale(1.1);
          box-shadow: 0 4px 8px rgba(0,0,0,0.3);
        }
        .modern-header-row {
          display: flex;
          flex-direction: row-reverse;
          gap: 24px;
          margin-bottom: 32px;
        }
        
        .modern-empty-card {
          border-radius: 16px !important;
          box-shadow: 0 2px 12px rgba(0,0,0,0.06) !important;
          background: #fff !important;
          padding: 48px 0 !important;
        }
      `}</style>
      <div style={containerStyle}>
        <button
          style={tourButtonStyle}
          onClick={handleTourButtonClick}
          title="הפעל סיור מודרך"
          className="suppliers-guide-button modern-tour-btn"
        >
          <FaQuestionCircle size={20} />
        </button>
        <div className="modern-header-row mb-4">
          <h2 className="modern-title mb-0">ספקים</h2>
          <InputGroup
            className="search-input"
            style={{ maxWidth: 400, width: "100%" }}
          >
            <Form.Control
              placeholder="חיפוש מוצרים או ספקים..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="modern-search-input text-end"
              dir="rtl"
            />
          </InputGroup>
          <Form.Select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="modern-category-select text-end category-select"
            style={{ width: "100%" }}
            dir="rtl"
            style={{ maxWidth: 200 }}
          >
            {categories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </Form.Select>
          {/* <Button
            variant="primary"
            onClick={() => openModal(MODAL_TYPES.EDIT)}
            className="modern-add-btn d-flex align-items-center gap-2 add-supplier-button"
          >
            <FaPlus /> הוסף ספק
          </Button> */}
        </div>
        <Row xs={1} md={2} lg={3} className="g-4 flex-row-reverse">
          {filteredSuppliers.map((supplier) => (
            <Col key={supplier.id}>
              <div className="modern-supplier-card">
                <SupplierCard
                  supplier={supplier}
                  onCardClick={handleCardClick}
                  className="supplier-card"
                />
              </div>
            </Col>
          ))}
          {filteredSuppliers.length === 0 && (
            <Col xs={12}>
              <Card className="modern-empty-card text-center p-5">
                <Card.Body>
                  <p className="modern-empty-message mb-0">
                    לא נמצאו ספקים התואמים את החיפוש
                  </p>
                </Card.Body>
              </Card>
            </Col>
          )}
        </Row>
        {/* Details Modal */}
        <div className="modern-modal">
          <SupplierDetailsModal
            show={currentModal === MODAL_TYPES.DETAILS}
            onHide={closeModal}
            supplier={currentSupplier}
            triggerPosition={modalPosition}
            onDeleteClick={handleDeleteClick}
            onEditClick={handleEditClick}
          />
        </div>
        {/* Edit/Add Modal */}
        <div className="modern-modal">
          <AddSupplierModal
            show={currentModal === MODAL_TYPES.EDIT}
            onHide={closeModal}
            onAddSupplier={handleAddSupplier}
            onUpdateSupplier={handleUpdateSupplier}
            editingSupplier={currentSupplier}
          />
        </div>
        {/* Delete Confirmation Modal */}
        <Modal
          show={currentModal === MODAL_TYPES.DELETE}
          onHide={closeModal}
          centered
          dir="rtl"
          className="modern-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>אישור מחיקה</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="d-flex align-items-center mb-3">
              <FaExclamationTriangle className="text-warning me-2 fs-4" />
              <p className="mb-0">
                האם אתה בטוח שברצונך למחוק את הספק "{currentSupplier?.name}"?
              </p>
            </div>
            <p className="text-muted">פעולה זו אינה ניתנת לביטול.</p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeModal}>
              ביטול
            </Button>
            <Button variant="danger" onClick={handleConfirmDelete}>
              מחק
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
      <Joyride
        steps={steps}
        run={runTour}
        continuous
        showProgress
        showSkipButton
        disableScrolling={true}
        callback={handleJoyrideCallback}
        styles={{
          options: {
            arrowColor: "#fff",
            backgroundColor: "#fff",
            overlayColor: "rgba(0, 0, 0, 0.5)",
            primaryColor: "#0066CC",
            textColor: "#333",
            zIndex: 1000,
          },
          tooltipContainer: {
            direction: "rtl",
            textAlign: "right",
          },
          buttonNext: {
            backgroundColor: "#0066CC",
            fontSize: 14,
            padding: "8px 16px",
          },
          buttonBack: {
            marginRight: 10,
            fontSize: 14,
            padding: "8px 16px",
          },
          buttonSkip: {
            color: "#666",
            fontSize: 14,
            padding: "8px 16px",
          },
        }}
        locale={{
          back: "הקודם",
          close: "סגור",
          last: "סיום",
          next: "הבא",
          skip: "דלג",
        }}
      />
    </div>
  );
};

export default SuppliersPane;
